import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { ApiService } from '../api/api.service';

/**
 * Interface for course object within student course response
 */
export interface StudentCourse {
    id: string;
    name: string;
    description: string;
    thumbnailUrl?: string;
    tags: string[];
    status: 'draft' | 'published' | 'archived';
}

/**
 * Interface for package instance reference within student course assignment
 */
export interface PackageInstanceReference {
    templateId: string;
    status: 'pending_payment' | 'active' | 'expired' | 'cancelled';
    id: string;
}

/**
 * Interface for payment reference within student course assignment
 */
export interface PaymentReference {
    _id: string;
    paymentToken: string;
    totalAmount: number;
    status: 'pending' | 'approved' | 'rejected' | 'cancelled';
}

/**
 * Interface for student course assignment
 */
export interface StudentCourseAssignment {
    id: string;
    course: StudentCourse;
    status: 'pending' | 'active' | 'completed' | 'suspended';
    assignedAt: string;
    activatedAt?: string;
    completedAt?: string;
    expiresAt?: string;
    progress: CourseProgress;
    packageInstanceId: PackageInstanceReference;
    paymentId: PaymentReference;
}

/**
 * Interface for lesson within course structure (from API response)
 */
export interface CourseLesson {
    _id: string;
    title: string;
    type: 'content' | 'video' | 'quiz' | 'exam';
    description?: string;
    duration?: number;
    order: number;
    content?: string;
    videoUrl?: string;
    quizContent?: any;
    isPublished?: boolean;
    isCompleted?: boolean;
    createdBy?: string;
    createdAt?: string;
    updatedAt?: string;
}

/**
 * Interface for module within course structure (from API response)
 */
export interface CourseModule {
    _id: string;
    name: string;
    description?: string;
    index: number;
    lessons: CourseLesson[]; // Array of lesson objects (not just IDs)
    dependsOn: string[];
    thumbnailUrl?: string;
    createdBy?: string;
    createdAt?: string;
    updatedAt?: string;
    fileId?: string;
}

/**
 * Interface for subject within course structure (from API response)
 */
export interface CourseSubject {
    _id: string;
    name: string;
    description?: string;
    order: number;
    modules: CourseModule[];
    thumbnailUrl?: string;
    createdBy?: string;
    createdAt?: string;
    updatedAt?: string;
    fileId?: string;
}

/**
 * Interface for course progress information
 */
export interface CourseProgress {
    completedLessons: number;
    totalLessons: number;
    percentage: number;
}

/**
 * Interface for full course instance with structure (from API response)
 */
export interface CourseInstance {
    id: string;
    course: {
        id: string;
        name: string;
        description: string;
        thumbnailUrl?: string;
        status: string;
        subjects: CourseSubject[];
    };
    status: 'pending' | 'active' | 'completed' | 'inactive' | 'expired';
    completedLessonIds: string[];
    assignedAt: string;
    activatedAt?: string;
    lastAccessedAt?: string;
    packageInstanceId: any;
    paymentId: any;
}

/**
 * Interface for quiz answer submission
 */
export interface QuizAnswer {
    questionIndex: number;
    selectedAnswer: number;
}

/**
 * Interface for lesson completion request data
 */
export interface LessonCompletionRequest {
    quizAnswers?: QuizAnswer[];
}


/**
 * Interface for lesson completion response
 */
export interface LessonCompletionResponse {
    message: string;
    progress: CourseProgress;
    courseCompleted?: boolean;
}

/**
 * Service for managing student-related operations
 * 
 * This service handles:
 * - Fetching student courses
 * - Managing course progress
 * - Other student-specific operations
 * 
 * @dependencies
 * - ApiService: For API communications
 * 
 * @exports StudentService
 */
@Injectable({
    providedIn: 'root',
})
export class StudentService {
    private _apiService = inject(ApiService);
    
    // Private behavior subjects
    private _courses: BehaviorSubject<StudentCourseAssignment[]> = new BehaviorSubject<StudentCourseAssignment[]>([]);
    private _currentCourseInstance: BehaviorSubject<CourseInstance | null> = new BehaviorSubject<CourseInstance | null>(null);
    
    // Cache for lesson types to avoid repeated API calls
    private _lessonTypeCache: Map<string, 'content' | 'video' | 'quiz' | 'exam'> = new Map();
    
    // Public observables
    get courses$(): Observable<StudentCourseAssignment[]> {
        return this._courses.asObservable();
    }
    
    get currentCourseInstance$(): Observable<CourseInstance | null> {
        return this._currentCourseInstance.asObservable();
    }
    
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------
    
    /**
     * Get all courses assigned to the current student
     */
    getMyCourses(): Observable<StudentCourseAssignment[]> {
        return this._apiService.get<StudentCourseAssignment[]>('student/my-courses').pipe(
            tap((courses) => {
                this._courses.next(courses);
            })
        );
    }
    
    /**
     * Get a specific course by ID from the local state
     * 
     * @param courseId - The ID of the course to retrieve
     * @returns The course assignment if found, null otherwise
     */
    getCourseById(courseId: string): StudentCourseAssignment | null {
        const courses = this._courses.value;
        return courses.find(assignment => assignment.course.id === courseId) || null;
    }
    
    /**
     * Get a course instance with full structure and progress
     * 
     * @param instanceId - The course instance ID
     * @returns Observable of the course instance
     */
    getCourseInstance(instanceId: string): Observable<CourseInstance> {
        return this._apiService.get<CourseInstance>(`student/course-instances/${instanceId}`).pipe(
            tap((courseInstance) => {
                this._currentCourseInstance.next(courseInstance);
            })
        );
    }
    
    /**
     * Mark a lesson as completed
     * 
     * @param instanceId - The course instance ID
     * @param lessonId - The lesson ID to mark as completed
     * @param completionData - Optional completion data (e.g., quiz answers for quiz/exam lessons)
     * @returns Observable of the completion response
     */
    markLessonComplete(instanceId: string, lessonId: string, completionData?: LessonCompletionRequest): Observable<LessonCompletionResponse> {
        return this._apiService.post<LessonCompletionResponse>(
            `student/course-instances/${instanceId}/lessons/${lessonId}/complete`,
            completionData || {}
        ).pipe(
            tap(() => {
                // Refresh the current course instance to get updated progress
                this.getCourseInstance(instanceId).subscribe();
            })
        );
    }
    
    /**
     * Mark a lesson as incomplete (undo completion)
     * 
     * @param instanceId - The course instance ID
     * @param lessonId - The lesson ID to mark as incomplete
     * @returns Observable of the completion response
     */
    markLessonIncomplete(instanceId: string, lessonId: string): Observable<LessonCompletionResponse> {
        return this._apiService.delete<LessonCompletionResponse>(
            `student/course-instances/${instanceId}/lessons/${lessonId}/complete`
        ).pipe(
            tap(() => {
                // Refresh the current course instance to get updated progress
                this.getCourseInstance(instanceId).subscribe();
            })
        );
    }
    
    /**
     * Get the current course instance from local state
     * 
     * @returns The current course instance or null
     */
    getCurrentCourseInstance(): CourseInstance | null {
        return this._currentCourseInstance.value;
    }
    
    /**
     * Calculate progress for a course instance
     * 
     * @param courseInstance - The course instance to calculate progress for
     * @returns Progress information
     */
    calculateProgress(courseInstance: CourseInstance): CourseProgress {
        let totalLessons = 0;
        let completedLessons = 0;
        
        // Count all lessons and completed lessons
        for (const subject of courseInstance.course.subjects) {
            for (const module of subject.modules) {
                totalLessons += module.lessons.length;
                // Count completed lessons using the isCompleted flag
                completedLessons += module.lessons.filter(lesson => lesson.isCompleted).length;
            }
        }
        
        const percentage = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
        
        return {
            completedLessons,
            totalLessons,
            percentage
        };
    }
    
    /**
     * Check if a lesson is completed
     * 
     * @param lessonId - The lesson ID to check
     * @returns True if the lesson is completed
     */
    isLessonCompleted(lessonId: string): boolean {
        const courseInstance = this._currentCourseInstance.value;
        if (!courseInstance) {
            return false;
        }
        
        // Find the lesson in the course structure and check its isCompleted status
        for (const subject of courseInstance.course.subjects) {
            for (const module of subject.modules) {
                const lesson = module.lessons.find(l => l._id === lessonId);
                if (lesson) {
                    return lesson.isCompleted || false;
                }
            }
        }
        
        // Fallback to the old method if lesson not found in structure
        return courseInstance.completedLessonIds.includes(lessonId);
    }
    
    /**
     * Find a lesson by ID within the current course instance
     * 
     * @param lessonId - The lesson ID to find
     * @returns The lesson object if found, null otherwise
     */
    findLessonById(lessonId: string): CourseLesson | null {
        const courseInstance = this._currentCourseInstance.value;
        if (!courseInstance) {
            return null;
        }
        
        // Search through all subjects and modules to find the lesson
        for (const subject of courseInstance.course.subjects) {
            for (const module of subject.modules) {
                const lesson = module.lessons.find(l => l._id === lessonId);
                if (lesson) {
                    return lesson;
                }
            }
        }
        
        return null;
    }
    
    /**
     * Get all lesson IDs in order for navigation purposes
     * 
     * @returns Array of all lesson IDs in the current course instance
     */
    getAllLessonIdsInOrder(): string[] {
        const courseInstance = this._currentCourseInstance.value;
        if (!courseInstance) {
            return [];
        }
        
        const lessonIds: string[] = [];
        
        // Sort subjects by order
        const sortedSubjects = [...courseInstance.course.subjects].sort((a, b) => a.order - b.order);
        
        for (const subject of sortedSubjects) {
            // Sort modules by index
            const sortedModules = [...subject.modules].sort((a, b) => a.index - b.index);
            
            for (const module of sortedModules) {
                // Sort lessons by order and add their IDs
                const sortedLessons = [...module.lessons].sort((a, b) => a.order - b.order);
                lessonIds.push(...sortedLessons.map(lesson => lesson._id));
            }
        }
        
        return lessonIds;
    }
    
    /**
     * Get the next lesson ID in the course sequence
     * 
     * @param currentLessonId - The current lesson ID
     * @returns The next lesson ID or null if at the end
     */
    getNextLessonId(currentLessonId: string): string | null {
        const lessonIds = this.getAllLessonIdsInOrder();
        const currentIndex = lessonIds.findIndex(id => id === currentLessonId);
        
        if (currentIndex === -1 || currentIndex === lessonIds.length - 1) {
            return null;
        }
        
        return lessonIds[currentIndex + 1];
    }
    
    /**
     * Get the previous lesson ID in the course sequence
     * 
     * @param currentLessonId - The current lesson ID
     * @returns The previous lesson ID or null if at the beginning
     */
    getPreviousLessonId(currentLessonId: string): string | null {
        const lessonIds = this.getAllLessonIdsInOrder();
        const currentIndex = lessonIds.findIndex(id => id === currentLessonId);
        
        if (currentIndex <= 0) {
            return null;
        }
        
        return lessonIds[currentIndex - 1];
    }
    
    /**
     * Get lesson details by ID from the course instance structure
     * 
     * @param lessonId - The lesson ID
     * @returns Observable of lesson details
     */
    getLessonDetails(lessonId: string): Observable<CourseLesson> {
        const lesson = this.findLessonById(lessonId);
        
        if (lesson) {
            // Cache the lesson type for future use
            this._lessonTypeCache.set(lessonId, lesson.type);
            return new Observable(observer => {
                observer.next(lesson);
                observer.complete();
            });
        } else {
            // Fallback to API call if lesson not found in structure
            return this._apiService.get<CourseLesson>(`lessons/${lessonId}`).pipe(
                tap(lesson => {
                    // Cache the lesson type for future use
                    this._lessonTypeCache.set(lessonId, lesson.type);
                })
            );
        }
    }
    
    /**
     * Get cached lesson type by ID
     * 
     * @param lessonId - The lesson ID
     * @returns The cached lesson type or null if not cached
     */
    getCachedLessonType(lessonId: string): 'content' | 'video' | 'quiz' | 'exam' | null {
        return this._lessonTypeCache.get(lessonId) || null;
    }
    
    /**
     * Get lesson info with completion status
     * 
     * @param lessonId - The lesson ID
     * @returns Basic lesson info or null
     */
    getLessonInfo(lessonId: string): { id: string; isCompleted: boolean } | null {
        const courseInstance = this._currentCourseInstance.value;
        if (!courseInstance) {
            return null;
        }
        
        return {
            id: lessonId,
            isCompleted: courseInstance.completedLessonIds.includes(lessonId)
        };
    }
    
    /**
     * Clear all courses from local state
     */
    clearCourses(): void {
        this._courses.next([]);
    }
    
    /**
     * Clear current course instance from local state
     */
    clearCurrentCourseInstance(): void {
        this._currentCourseInstance.next(null);
    }
}
